import React, { useEffect } from 'react';
import axios from 'axios';
import { useToast } from 'store/toast';
import { useTranslation } from 'react-i18next';
import apiEventEmitter from 'core/EventEmitter';
import { toast } from 'react-toastify';

const ApiErrorListener: React.FC = () => {
  const { notify } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    const showError = async (originalMessage: string) => {
      let displayMessage = t(originalMessage);

      try {
        // Attempt to fetch a fallback message from the backup API
        const backupApiUrl = process.env.REACT_APP_BACKUP_API_URL!;
        const response = await axios.get(backupApiUrl);
        if (response.data && response.data.message) {
          displayMessage = response.data.message;
        }
      } catch (backupError) {
        // If the backup API also fails, log the error and use the translated original message
        console.error('Backup API failed:', backupError);
      }

      notify({
        type: 'error',
        message: displayMessage,
        autoClose: false,
        toastId: 'apiError',
      });
    };

    const showMessage = (message: string) => {
      notify({
        type: 'warning',
        message: `${message}`,
        autoClose: false,
        toastId: 'apiMessage',
      });
    };

    const clearMessage = () => {
      toast.dismiss('apiMessage');
    };

    const clearApiError = () => {
      toast.dismiss('apiError');
    };

    apiEventEmitter.on('api-error', showError);
    apiEventEmitter.on('api-error-clear', clearApiError);
    apiEventEmitter.on('api-message', showMessage);
    apiEventEmitter.on('api-message-clear', clearMessage);

    return () => {
      apiEventEmitter.off('api-error', showError);
      apiEventEmitter.off('api-error-clear', clearApiError);
      apiEventEmitter.off('api-message', showMessage);
      apiEventEmitter.off('api-message-clear', clearMessage);
    };
  }, [notify]);

  return null;
};

export default ApiErrorListener;
